import { Hidden } from '@mui/material';
import { FormSelect } from 'components/common/FormSelect';
import { userRoles } from 'constants/constants';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setDashboardProject, Team } from 'redux/projectSlice';
import { RootStates } from 'redux/store';
import { MajorMetrics } from './components/MajorMetrics';
import { Summary } from './components/Summary';
import { WorkFlowRecord } from './components/WorkFlowRecord';
import { YourSummary } from './components/YourSummary';
import './Dashboard.scss';

const all = { userId: 'ALL', name: 'All', id: 'ALL' } as Team;

export const Dashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const projects = useSelector((state: RootStates) => state.project.projects);
  const selectedProject = useSelector(
    (state: RootStates) => state.project.dashboardProject,
  );
  const user = useSelector((state: RootStates) => state.auth.userDetails);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  const handleSelectProject = (project: any) => {
    dispatch(setDashboardProject(project));
  };

  const handleSelectTeam = (team: Team) => {
    if (selectedTeam?.userId !== team.userId) {
      setSelectedTeam(team);
    }
  };

  useEffect(() => {
    if (!selectedProject && projects?.length > 0) {
      dispatch(setDashboardProject(projects[0]));
    }
  }, [selectedProject, projects, dispatch]);

  useEffect(() => {
    if (user?._id && selectedProject?.projectInfo.id && !selectedTeam) {
      if (
        selectedProject.role === userRoles.OWNER ||
        selectedProject.role === userRoles.PROJECT_ADMIN
      ) {
        const projectUser =
          selectedProject.team.find((t) => t.userId === user._id) ?? null;
        if (projectUser) {
          setSelectedTeam({
            ...projectUser,
            name: 'You',
          });
        }
      }
    }
  }, [user, selectedProject, selectedTeam]);

  const usersDropdownOption = useMemo(() => {
    if (selectedProject) {
      const options = selectedProject.team?.map((t) =>
        t.userId === user?._id ? { ...t, name: 'You' } : t,
      );

      return [all, ...options];
    }

    return [];
  }, [selectedProject, user]);

  const renderByYouDropdown = () => {
    if (
      selectedProject?.role === userRoles.OWNER ||
      selectedProject?.role === userRoles.PROJECT_ADMIN
    ) {
      return (
        <FormSelect
          className="notification__project-select"
          placeholder="Select User"
          selectedItem={selectedTeam}
          items={usersDropdownOption}
          onSelect={handleSelectTeam}
        />
      );
    }

    return null;
  };

  return (
    <div className="d-flex flex-column overflow-auto w-100 h-100">
      <div className="d-flex mb-2 mt-2 gap-3 flex-wrap">
        <FormSelect
          className="notification__project-select"
          placeholder={t('label_select_project')}
          selectedItem={selectedProject}
          items={projects
            ?.filter((p) => p.isLaunched)
            .map((p) => ({
              ...p,
              id: p.projectInfo.projectId,
              name: p.projectInfo.projectName,
            }))}
          uniqueId="id"
          onSelect={handleSelectProject}
        />
        {renderByYouDropdown()}
      </div>
      <Hidden mdDown>
        <div className="dashboard gap-2">
          <div className="col1 gap-2">
            <WorkFlowRecord
              projectId={selectedProject?.projectInfo.id ?? ''}
              pcDate={selectedProject?.created_at ?? ''}
              userId={selectedTeam?.userId ?? user?._id ?? ''}
            />
            <MajorMetrics
              projectId={selectedProject?.projectInfo.id ?? ''}
              userId={selectedTeam?.userId ?? user?._id ?? ''}
            />
          </div>
          <div className="col2 gap-2">
            <YourSummary
              project={selectedProject}
              userId={selectedTeam?.userId ?? user?._id ?? ''}
              role={selectedTeam?.role ?? selectedProject?.role ?? ''}
            />
            <Summary
              projectId={selectedProject?.projectInfo.id ?? ''}
              userId={selectedTeam?.userId ?? user?._id ?? ''}
            />
          </div>
        </div>
      </Hidden>
      <Hidden mdUp>
        <div className="dashboard flex-column gap-2">
          <WorkFlowRecord
            projectId={selectedProject?.projectInfo.id ?? ''}
            pcDate={selectedProject?.created_at ?? ''}
            userId={selectedTeam?.userId ?? user?._id ?? ''}
          />
          <YourSummary
            project={selectedProject}
            userId={selectedTeam?.userId ?? user?._id ?? ''}
            role={selectedTeam?.role ?? selectedProject?.role ?? ''}
          />
          <MajorMetrics
            projectId={selectedProject?.projectInfo.id ?? ''}
            userId={selectedTeam?.userId ?? user?._id ?? ''}
          />
          <Summary
            isMobile
            projectId={selectedProject?.projectInfo.id ?? ''}
            userId={selectedTeam?.userId ?? user?._id ?? ''}
          />
        </div>
      </Hidden>
    </div>
  );
};

export default Dashboard;
